import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    IconButton, Menu, MenuItem, Dialog, DialogActions, 
    DialogContent, DialogContentText, DialogTitle, Button, Box, Typography
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { getMedicines, deleteMedicine } from './api';
import MedicinesFilterDrawer from './MedicinesFilterDrawer';
import AddMedicineDrawer from './AddMedicineDrawer';
import EditMedicineDrawer from './EditMedicineDrawer';
import TopHeader from "../../components/common/Header/TopHeader";
import DoctorManagementMenu from "../../components/common/Menu/DoctorManagement";
import MedicineKPI from './MedicineKPI';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const MedicinesList = () => {
    const [medicines, setMedicines] = useState([]);
    const [filteredMedicines, setFilteredMedicines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [selectedMedicine, setSelectedMedicine] = useState(null);
    const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
    const [actionRow, setActionRow] = useState(null);
    const [filters, setFilters] = useState({});
    const [showKPIs, setShowKPIs] = useState(true);
    const [rowSelection, setRowSelection] = useState({});
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [actionsAnchorEl, setActionsAnchorEl] = useState(null);

    const [appliedFilters, setAppliedFilters] = useState({});

    const fetchMedicines = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getMedicines();
            setMedicines(data);
            applyFilters(data, appliedFilters, searchTerm);
        } catch (err) {
            console.error('Error al obtener los medicamentos', err);
        } finally {
            setLoading(false);
        }
    }, [appliedFilters, searchTerm]);

    useEffect(() => {
        fetchMedicines();
    }, [fetchMedicines]);

    const handleEdit = (medicine) => {
        setSelectedMedicine(medicine);
        setEditDrawerOpen(true);
    };

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this medicine?');
        if (confirmDelete) {
            try {
                await deleteMedicine(id);
                fetchMedicines();
            } catch (err) {
                console.error('Error al eliminar medicamento:', err);
            }
        }
    };

    const handleBulkDelete = async () => {
        const ids = Object.keys(rowSelection).map(index => filteredMedicines[index]._id);
        for (let id of ids) {
            await deleteMedicine(id);
        }
        setDeleteConfirmOpen(false);
        setRowSelection({});
        fetchMedicines();
    };

    const applyFilters = (data, filters = {}, search = "") => {
        let filtered = [...data];
        
        // Aplicar búsqueda global
        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(m =>
                m.name?.toLowerCase().includes(searchLower) ||
                m.manufacturer?.toLowerCase().includes(searchLower) ||
                m.sku?.toLowerCase().includes(searchLower) ||
                m.ndc?.toLowerCase().includes(searchLower) ||
                m.description?.toLowerCase().includes(searchLower)
            );
        }

        // Aplicar filtros específicos
        if (filters.manufacturer) {
            filtered = filtered.filter(m => m.manufacturer?.toLowerCase().includes(filters.manufacturer?.toLowerCase()));
        }
        if (filters.category) {
            filtered = filtered.filter(m => m.category?.toLowerCase().includes(filters.category?.toLowerCase()));
        }
        if (filters.dosage) {
            filtered = filtered.filter(m => m.specifications?.dosage.toLowerCase().includes(filters.dosage?.toLowerCase()));
        }
        if (filters.form) {
            filtered = filtered.filter(m => m.specifications?.form.toLowerCase().includes(filters.form?.toLowerCase()));
        }
        if (filters.status) {
            filtered = filtered.filter(m => m.status?.toLowerCase().includes(filters.status?.toLowerCase()));
        }

        setFilteredMedicines(filtered);
    };

    const handleFilterClick = () => {
        setDrawerOpen(true);
    };

    const handleApplyFilters = (newFilters) => {
        setAppliedFilters(newFilters);
        setDrawerOpen(false);
        applyFilters(medicines, newFilters, searchTerm);
    };

    const handleRemoveFilter = (filterKey) => {
        const newFilters = { ...appliedFilters };
        delete newFilters[filterKey];
        setAppliedFilters(newFilters);
        applyFilters(medicines, newFilters, searchTerm);
    };

    const handleClearAll = () => {
        setAppliedFilters({});
        applyFilters(medicines, {}, searchTerm);
    };

    const exportCSV = () => {
        const headers = ['ID', 'Name', 'Description', 'Price', 'Stock', 'Dosage', 'Expiry Date'];
        const rows = filteredMedicines.map(m => [
            m.id,
            m.name,
            m.description,
            m.price,
            m.stock,
            m.specifications?.dosage,
            m.specifications?.expiryDate ? new Date(m.specifications.expiryDate).toLocaleDateString() : ''
        ]);
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'medicines.csv';
        a.click();
    };

    const exportExcel = () => {
        const ws = XLSX.utils.json_to_sheet(filteredMedicines);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Medicines');
        XLSX.writeFile(wb, 'medicines.xlsx');
    };

    const exportPDF = () => {
        const doc = new jsPDF();
        const headers = [['ID', 'Name', 'Description', 'Price', 'Stock', 'Dosage', 'Expiry Date']];
        const data = filteredMedicines.map(m => [
            m.id,
            m.name,
            m.description,
            `$${m.price?.toFixed(2)}`,
            m.stock,
            m.specifications?.dosage,
            m.specifications?.expiryDate ? new Date(m.specifications.expiryDate).toLocaleDateString() : ''
        ]);
        doc.autoTable({ head: headers, body: data });
        doc.save('medicines.pdf');
    };

    const totalMedicines = medicines.length;
    const manufacturers = new Set(medicines.map((m) => m.manufacturer)).size;
    const categories = new Set(medicines.map((m) => m.category)).size;
    const avgPrice = medicines.length > 0 
        ? (medicines.reduce((acc, m) => acc + m.price, 0) / medicines.length).toFixed(2) 
        : 0;

    const columns = useMemo(() => [
        {
            accessorKey: "actions",
            header: "Actions",
            Cell: ({ row }) => {
                const [anchorEl, setAnchorEl] = useState(null);
                const open = Boolean(anchorEl);

                const handleClick = (event) => {
                    setAnchorEl(event.currentTarget);
                };

                const handleClose = () => {
                    setAnchorEl(null);
                };

                const handleMenuAction = (action) => {
                    handleClose();
                    action();
                };

                return (
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                        <IconButton onClick={handleClick}>
                            <p>...</p>
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                            <MenuItem onClick={() => handleMenuAction(() => {
                                setActionRow(row.original);
                                setDetailsOpen(true);
                            })}>
                                View Details
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuAction(() => handleEdit(row.original))}>
                                Edit
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuAction(() => window.print())}>
                                Print
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuAction(() => handleDelete(row.original._id))}>
                                Delete
                            </MenuItem>
                        </Menu>
                    </div>
                );
            },
            size: 50,
        },
        {
            accessorKey: "name",
            header: "Name",
            Cell: ({ row }) => {
                const { name, imageUrl } = row.original;
                return (
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                        {imageUrl ? (
                            <img
                                src={imageUrl}
                                alt={name}
                                className="rounded-circle me-2"
                                style={{ width: 30, height: 30, objectFit: "cover" }}
                                onError={e => {
                                    e.target.onerror = null;
                                    e.target.style.display = "none";
                                }}
                            />
                        ) : (
                            <div
                                className="rounded-circle me-2"
                                style={{
                                    width: 30,
                                    height: 30,
                                    backgroundColor: "#ccc",
                                }}
                            />
                        )}
                        <span className="text-truncate">{name}</span>
                    </div>
                );
            },
        },
        { 
            accessorKey: 'id', 
            header: 'ID',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center text-truncate" style={{ height: '60px' }}>
                    {row.original.id}
                </div>
            )
        },
        { 
            accessorKey: 'description', 
            header: 'Description',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px', maxWidth: '300px' }}>
                    <span className="text-truncate d-inline-block" style={{ maxWidth: '100%' }}>
                        {row.original.description}
                    </span>
                </div>
            )
        },
        {
            accessorKey: 'price',
            header: 'Price',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px' }}>
                    {`$${row.original.price?.toFixed(2)}`}
                </div>
            )
        },
        { 
            accessorKey: 'stock', 
            header: 'Stock',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px' }}>
                    {row.original.stock}
                </div>
            )
        },
        { 
            accessorKey: 'specifications.dosage', 
            header: 'Dosage',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center text-truncate" style={{ height: '60px' }}>
                    {row.original.specifications?.dosage}
                </div>
            )
        },
        {
            accessorKey: 'specifications.expiryDate',
            header: 'Expiry Date',
            Cell: ({ row }) => {
                const expiry = row.original.specifications?.expiryDate;
                return (
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                        {expiry ? new Date(expiry).toLocaleDateString() : 'No expiry date'}
                    </div>
                );
            }
        }
    ], []);

    const activeFilterCount = Object.keys(appliedFilters).filter(key => appliedFilters[key]).length;

    return (
        <main id="app">
            <TopHeader />

            <div className="content_wrapper">
                <section className="doctor-management-wrapper bg-white pb-5">
                    
                    {/* Inner Menu */}
                    <DoctorManagementMenu activeMenu='medicines'/>

                    {/* Body Container */}
                    <div className="container-fluid px-lg-5">

                        {/* Breadcrumb */}
                        <nav className="breadcrumb-container d-none d-md-block pt-3 mb-2" aria-label="breadcrumb">
                            <ol className="breadcrumb fs-sm mb-0">
                                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Medicines</li>
                            </ol>
                        </nav>

                        {/* KPI Section */}
                        <MedicineKPI 
                            totalMedicines={totalMedicines}
                            manufacturers={manufacturers}
                            categories={categories}
                            avgPrice={avgPrice}
                            showKPIs={showKPIs}
                        />

                        {/* Action Buttons */}
                        <div className="filter-container py-3">
                            <div className="row align-items-center">

                                <div className="col-md-5 col-lg-6 d-none d-md-block mb-3 mb-md-0">
                                    <div className="position-relative">
                                        <div className="input-group">
                                            <button type="submit" className="p-0 bg-transparent border-0">
                                                <span className="d-block material-symbols-outlined icon-lg ">search</span>
                                            </button>
                                            <input 
                                                type="text"
                className="form-control ps-2 bg-transparent border-0 shadow-none"
                                                placeholder="Search medicines..."
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    applyFilters(medicines, appliedFilters, e.target.value);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-1 gap-sm-2 gy-3 flex-wrap">
                                    {/* Action dropdown */}
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-gray text-start w-100 d-flex align-items-center justify-content-between px-4 py-2 border-0 rounded-3 shadow-sm dropdownbtn btn btn-gray dropdown-toggle d-flex align-items-center gap-1"
                                            type="button"
                                            onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                                        >
                                            <span className="d-block">Action</span>
                                        </button>
                                        <Menu
                                            anchorEl={actionsAnchorEl}
                                            open={Boolean(actionsAnchorEl)}
                                            onClose={() => setActionsAnchorEl(null)}
                                        >
                                            <MenuItem onClick={() => {
                                                exportCSV();
                                                setActionsAnchorEl(null);
                                            }}>
                                                Export CSV
                                            </MenuItem>
                                            <MenuItem onClick={() => {
                                                exportExcel();
                                                setActionsAnchorEl(null);
                                            }}>
                                                Export Excel
                                            </MenuItem>
                                            <MenuItem onClick={() => {
                                                exportPDF();
                                                setActionsAnchorEl(null);
                                            }}>
                                                Export PDF
                                            </MenuItem>
                                            <MenuItem onClick={() => {
                                                setDeleteConfirmOpen(true);
                                                setActionsAnchorEl(null);
                                            }}>
                                                Delete Selected
                                            </MenuItem>
                                        </Menu>
                                    </div>

                                    {/* Filter button */}
                                    <Link 
                                        to="#" 
                                        className="btn btn-gray filterbtn d-flex align-items-center gap-1 position-relative" 
                                        type="button" 
                                        onClick={handleFilterClick}
                                    >
                                        <div className="filter_text d-flex align-items-center gap-1">
                                            <span className="d-block material-symbols-outlined icon-md">tune</span>
                                            <span className="d-block">Filter</span>
                                        </div>
                                        {activeFilterCount > 0 && (
                                            <span 
                                                className="position-absolute top-0 start-100 translate-middle badge rounded-circle bg-primary" 
                                                style={{fontSize: '0.6rem', padding: '0.2rem 0.4rem'}}
                                            >
                                                {activeFilterCount}
                                            </span>
                                        )}
                                    </Link>
                                    
                                    {/* Add New button */}
                                    <Link 
                                        to="#" 
                                        className="btn btn-primary addnewbtn d-flex align-items-center gap-1" 
                                        onClick={() => setAddDrawerOpen(true)}
                                    >
                                        <span className="d-block material-symbols-outlined icon-md">add</span>
                                        <span className="d-block">Add New</span>
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Active Filters */}
                        {activeFilterCount > 0 && (
                            <div className="active-filters-container mb-3 p-3 bg-light rounded">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <div className="d-flex align-items-center flex-wrap gap-2">
                                        <strong className="me-2">Active Filters:</strong>
                                        {Object.entries(appliedFilters).map(([key, value]) => {
                                            if (!value) return null;
                                            return (
                                                <span key={key} className="badge bg-secondary d-flex align-items-center gap-1">
                                                    {key}: {value}
                                                    <button
                                                        type="button"
                                                        className="btn-close btn-close-white"
                                                        style={{fontSize: '0.5rem'}}
                                                        onClick={() => handleRemoveFilter(key)}
                                                    />
                                                </span>
                                            );
                                        })}
                                    </div>
                                    <button 
                                        className="btn btn-link text-primary p-0" 
                                        onClick={handleClearAll}
                                    >
                                        Clear All
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Table */}
                        <div className="table-wrapper">
                            <MaterialReactTable
                                columns={columns}
                                data={filteredMedicines}
                                state={{ isLoading: loading, rowSelection }}
                                onRowSelectionChange={setRowSelection}
                                enableRowSelection={true}
                                enableColumnFilters
                                enableColumnOrdering
                                enableSorting
                                enablePagination
                            />
                        </div>
                    </div>
                </section>
            </div>

            {/* Drawers and Dialogs */}
            <MedicinesFilterDrawer 
                open={drawerOpen} 
                onClose={() => setDrawerOpen(false)} 
                onFilterChange={handleApplyFilters}
                filters={appliedFilters}
            />
            
            <AddMedicineDrawer 
                open={addDrawerOpen} 
                onClose={() => setAddDrawerOpen(false)} 
                selectedMedicine={null} 
                onMedicineAdded={(newMedicine) => {
                    const updatedList = [...medicines, newMedicine];
                    setMedicines(updatedList);
                    applyFilters(updatedList, appliedFilters, searchTerm);
                }} 
            />
            
            <EditMedicineDrawer
                open={editDrawerOpen}
                onClose={() => setEditDrawerOpen(false)}
                selectedMedicine={selectedMedicine}
                onMedicineUpdated={(updated) => {
                    setMedicines(prev => prev.map(m => m._id === updated._id ? updated : m));
                    setFilteredMedicines(prev => prev.map(m => m._id === updated._id ? updated : m));
                }}
            />
            
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the selected medicines?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleBulkDelete} color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)}>
                <DialogTitle>Medicine Details</DialogTitle>
                <DialogContent dividers>
                    {actionRow && (
                        <Box>
                            <Typography variant="h6">{actionRow.name}</Typography>
                            <Typography><strong>ID:</strong> {actionRow.id}</Typography>
                            <Typography><strong>Description:</strong> {actionRow.description}</Typography>
                            <Typography><strong>Manufacturer:</strong> {actionRow.manufacturer}</Typography>
                            <Typography><strong>Category:</strong> {actionRow.category}</Typography>
                            <Typography><strong>Price:</strong> ${actionRow.price?.toFixed(2)}</Typography>
                            <Typography><strong>Stock:</strong> {actionRow.stock}</Typography>
                            <Typography><strong>Form:</strong> {actionRow.specifications?.form}</Typography>
                            <Typography><strong>Dosage:</strong> {actionRow.specifications?.dosage}</Typography>
                            <Typography><strong>Expiry Date:</strong> {actionRow.specifications?.expiryDate ? new Date(actionRow.specifications.expiryDate).toLocaleDateString() : 'N/A'}</Typography>
                            <Typography><strong>Status:</strong> {actionRow.status || 'N/A'}</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </main>
    );
};

export default MedicinesList;